import Icon from '@material-ui/core/Icon';
import clsx from 'clsx';
import React from 'react';
import Slider from 'react-slick';
import { useObservable } from 'react-use-observable';
import { EMPTY } from 'rxjs';
import { map } from 'rxjs/operators';
import { useElite } from 'v2/hooks/useElite';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { settingsFactory } from 'v2/services/settings';

import { useStyles } from './styles';

const TEN_SECONDS_IN_MS = 10000;

type ArrowProps = {
  onClick?: () => void;
  direction?: 'right' | 'left';
};

const Arrow = ({ onClick, direction = 'right' }: ArrowProps) => {
  const classes = useStyles();
  return (
    <div className={clsx(classes.arrow, `--${direction}`, !onClick && '--disabled')} onClick={onClick}>
      <Icon>{direction === 'right' ? 'chevron_right' : 'chevron_left'}</Icon>
    </div>
  );
};

const Banner = () => {
  const [isLoading] = useObservable(() => eduzzAccountsService.getLoadingAccount(), []);
  const classes = useStyles();
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [badge] = useObservable(() => settingsFactory.actualUserBadge(user?.id), [user]);
  const { data } = useElite();

  const [banners] = useObservable(() => {
    if (isLoading === null || isLoading) return EMPTY;

    const producerActualBadge = badge?.id;
    const producerIsInClubeBlack = !!user?.is_clube_black;

    return settingsFactory.getBanners().pipe(
      map((banners) =>
        banners.filter((banner) => {
          const bannerNotUnity = banner.unity == 0;
          const bannerIsUnity = banner.unity == 1;
          const bannerShowAll = banner.unity == 2;
          const gainsProducer = data?.gains_producer || 0;
          const isUserTarget = banner.targets?.some((target) => target.qualificationCode === producerActualBadge);
          const isWhite = banner.targets?.some((target) => target.qualificationCode === 323 && gainsProducer < 100000);

          if (producerIsInClubeBlack && (bannerIsUnity || bannerShowAll)) {
            return true;
          }

          if (!producerIsInClubeBlack && isUserTarget && (bannerNotUnity || bannerShowAll)) {
            return true;
          }

          if (!producerIsInClubeBlack && isWhite && (bannerNotUnity || bannerShowAll)) {
            return true;
          }

          return false;
        })
      )
    );
  }, [module, user, isLoading, badge]);

  if (!banners?.length) return null;

  return (
    <>
      <Slider
        infinite={false}
        autoplay
        autoplaySpeed={TEN_SECONDS_IN_MS}
        prevArrow={<Arrow direction='left' />}
        nextArrow={<Arrow direction='right' />}
      >
        {banners.map((banner) =>
          banner.link ? (
            <a key={banner.url} target='_blank' rel='noopener noreferrer' href={banner.link} className='banner-myeduzz'>
              <img className={classes.img} src={banner.url} alt='Banner' />
            </a>
          ) : (
            <div key={banner.url} className={`banner-myeduzz ${classes.imageDiv}`}>
              <img src={banner.url} alt='Banner' />
            </div>
          )
        )}
      </Slider>
      <div className='m16' />
    </>
  );
};

type ModuleProps = {
  module: string;
};

const Module = ({ module }: ModuleProps) => {
  const [isLoading] = useObservable(() => eduzzAccountsService.getLoadingAccount(), []);
  const classes = useStyles();

  const [banner] = useObservable(() => {
    if (isLoading === null || isLoading) return EMPTY;
    return settingsFactory.getBanner(module);
  }, []);

  if (!banner) return null;

  if (banner.link) {
    return (
      <a target='_blank' rel='noopener noreferrer' href={banner.link} id='banner-myeduzz'>
        <img className={classes.img} src={banner.url} alt='Banner' />
      </a>
    );
  }

  return (
    <>
      <div className={classes.imageDiv} id='banner-myeduzz'>
        <img src={banner.url} alt='Banner' />
      </div>
      <div className='m16' />
    </>
  );
};

Banner.Module = Module;

export default Banner;
